/**
 * SupervisionPage — Cockpit del supervisor (SUP-01).
 *
 * Muestra en una sola pantalla, agrupado por sala, los pacientes en SOLICITUD
 * (en_espera) y en ATENCIÓN (en_atencion, ya tomados por un médico). Desde aquí
 * el supervisor puede unirse como observador a una teleconsulta en curso.
 *
 * Las reglas de Firestore permiten a admin/supervisor leer cualquier consulta.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { where } from 'firebase/firestore';
import { Clock, Eye, Search, Stethoscope, Users, Radio } from 'lucide-react';
import MainLayout from '../components/layout/MainLayout';
import { Badge, Input } from '../components/ui';
import { useCollection } from '../hooks/useFirestore';
import type { Consulta } from '../types';
import styles from './SupervisionPage.module.css';

const ESTADOS_ACTIVOS = ['en_espera', 'en_atencion'] as const;

function transcurrido(ts: unknown): string {
  const d = (ts as { toDate?: () => Date } | undefined)?.toDate?.();
  if (!d) return '—';
  const seg = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (seg < 60) return `${seg}s`;
  const min = Math.floor(seg / 60);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  return `${h}h ${min % 60}m`;
}

interface SalaGrupo {
  salaId: string;
  salaNombre: string;
  enEspera: Consulta[];
  enAtencion: Consulta[];
}

export default function SupervisionPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const constraints = useMemo(() => [where('estado', 'in', [...ESTADOS_ACTIVOS])], []);
  const { data, loading } = useCollection<Consulta>('consultas', constraints);

  const grupos = useMemo<SalaGrupo[]>(() => {
    const q = search.trim().toLowerCase();
    const lista = (data ?? []).filter((c) =>
      !q ||
      c.pacienteNombre?.toLowerCase().includes(q) ||
      c.salaNombre?.toLowerCase().includes(q) ||
      c.medicoNombre?.toLowerCase().includes(q),
    );

    const mapa = new Map<string, SalaGrupo>();
    for (const c of lista) {
      const salaId = c.salaId || '—';
      let g = mapa.get(salaId);
      if (!g) {
        g = { salaId, salaNombre: c.salaNombre || salaId, enEspera: [], enAtencion: [] };
        mapa.set(salaId, g);
      }
      if (c.estado === 'en_atencion') g.enAtencion.push(c);
      else if (c.estado === 'en_espera') g.enEspera.push(c);
    }
    // Ordenar: pacientes más antiguos primero; salas con más actividad arriba.
    const grupos = Array.from(mapa.values());
    for (const g of grupos) {
      g.enEspera.sort((a, b) => (a.creadoEn?.toMillis?.() ?? 0) - (b.creadoEn?.toMillis?.() ?? 0));
      g.enAtencion.sort((a, b) => (a.iniciadaEn?.toMillis?.() ?? 0) - (b.iniciadaEn?.toMillis?.() ?? 0));
    }
    grupos.sort(
      (a, b) => (b.enEspera.length + b.enAtencion.length) - (a.enEspera.length + a.enAtencion.length),
    );
    return grupos;
  }, [data, search]);

  const totales = useMemo(() => {
    const enEspera = (data ?? []).filter((c) => c.estado === 'en_espera').length;
    const enAtencion = (data ?? []).filter((c) => c.estado === 'en_atencion').length;
    const salas = new Set((data ?? []).map((c) => c.salaId)).size;
    return { enEspera, enAtencion, salas };
  }, [data]);

  return (
    <MainLayout pageTitle="Supervisión" pageSubtitle="Pacientes en solicitud y en atención, por sala">
      <div className={styles.page}>
        {/* KPIs */}
        <div className={styles.kpis}>
          <div className={styles.kpi}>
            <Clock size={18} className={styles.kpiIcEsp} aria-hidden="true" />
            <div><span className={styles.kpiVal}>{totales.enEspera}</span><span className={styles.kpiLbl}>En espera</span></div>
          </div>
          <div className={styles.kpi}>
            <Radio size={18} className={styles.kpiIcAt} aria-hidden="true" />
            <div><span className={styles.kpiVal}>{totales.enAtencion}</span><span className={styles.kpiLbl}>En atención</span></div>
          </div>
          <div className={styles.kpi}>
            <Stethoscope size={18} className={styles.kpiIcSala} aria-hidden="true" />
            <div><span className={styles.kpiVal}>{totales.salas}</span><span className={styles.kpiLbl}>Salas con actividad</span></div>
          </div>
        </div>

        {/* Búsqueda */}
        <div className={styles.searchBar}>
          <Input
            placeholder="Buscar por paciente, sala o médico…"
            leftIcon={<Search size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Contenido */}
        {loading && !data ? (
          <p className={styles.empty}>Cargando…</p>
        ) : grupos.length === 0 ? (
          <div className={styles.empty}>
            <Users size={22} aria-hidden="true" />
            <p>No hay pacientes en espera ni en atención en este momento.</p>
          </div>
        ) : (
          <div className={styles.grupos}>
            {grupos.map((g) => (
              <section key={g.salaId} className={styles.salaCard}>
                <header className={styles.salaHead}>
                  <h2 className={styles.salaNombre}>{g.salaNombre}</h2>
                  <div className={styles.salaCounts}>
                    <Badge variant="warning" size="sm">{g.enEspera.length} en espera</Badge>
                    <Badge variant="success" size="sm">{g.enAtencion.length} en atención</Badge>
                  </div>
                </header>

                {/* En atención */}
                {g.enAtencion.length > 0 && (
                  <div className={styles.bloque}>
                    <div className={styles.bloqueTitulo}><Radio size={13} /> En atención</div>
                    <ul className={styles.filas}>
                      {g.enAtencion.map((c) => (
                        <li key={c.id} className={styles.fila}>
                          <div className={styles.filaInfo}>
                            <span className={styles.paciente}>{c.pacienteNombre}</span>
                            <span className={styles.meta}>
                              {c.medicoNombre ? `Dr. ${c.medicoNombre}` : 'Médico'} · {c.motivoConsulta || 'Sin motivo'}
                            </span>
                          </div>
                          <span className={styles.tiempo}><Clock size={12} /> {transcurrido(c.iniciadaEn)}</span>
                          <button
                            type="button"
                            className={styles.observarBtn}
                            onClick={() => navigate(`/consulta/${c.id}/observar`)}
                            title="Unirse como observador a esta teleconsulta"
                          >
                            <Eye size={14} /> Observar
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* En espera */}
                {g.enEspera.length > 0 && (
                  <div className={styles.bloque}>
                    <div className={styles.bloqueTitulo}><Clock size={13} /> En espera</div>
                    <ul className={styles.filas}>
                      {g.enEspera.map((c) => (
                        <li key={c.id} className={`${styles.fila} ${styles.filaEspera}`}>
                          <div className={styles.filaInfo}>
                            <span className={styles.paciente}>{c.pacienteNombre}</span>
                            <span className={styles.meta}>{c.motivoConsulta || 'Sin motivo'}</span>
                          </div>
                          <span className={styles.tiempo}><Clock size={12} /> {transcurrido(c.creadoEn)}</span>
                          <span className={styles.esperaHint}>Esperando médico</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </section>
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
}
